// src/pages/Events/components/ProfessionalTimeline.jsx
// 🎯 ENHANCED: Animated playback indicator + better visibility

import { useState, useRef, useEffect } from "react";
import { FiZoomIn, FiZoomOut, FiPlay, FiPause } from "react-icons/fi";

export default function ProfessionalTimeline({
  camera,
  recordings = [],
  events = [],
  bookmarks = [],
  currentTime,
  setCurrentTime,
  zoom = 1,
  setZoom,
  date,
  compact = false,
  playing = false, // ✅ ADDED: Know if playing
}) {
  const timelineRef = useRef(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const startOfDay = new Date(date + "T00:00:00");
  const endOfDay = new Date(date + "T23:59:59");

  const timeToPosition = (time) => {
    const totalMs = endOfDay - startOfDay;
    const elapsed = new Date(time) - startOfDay;
    return (elapsed / totalMs) * 100;
  };

  const positionToTime = (percent) => {
    const totalMs = endOfDay - startOfDay;
    const elapsed = (percent / 100) * totalMs;
    return new Date(startOfDay.getTime() + elapsed);
  };

  const handleInteraction = (clientX) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = (x / rect.width) * 100;
    const newTime = positionToTime(Math.max(0, Math.min(100, percent)));
    setCurrentTime(newTime);
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    handleInteraction(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) {
      if (!compact && window.innerWidth >= 768) {
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = (x / rect.width) * 100;
        const time = positionToTime(percent);
        setHoveredTime(time);
      }
      return;
    }
    handleInteraction(e.clientX);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };
    }
  }, [isDragging]);

  const getHourMarks = () => {
    if (compact) {
      return [0, 6, 12, 18];
    }
    const step = zoom >= 4 ? 1 : zoom >= 2 ? 2 : 4;
    const marks = [];
    for (let hour = 0; hour < 24; hour += step) {
      marks.push(hour);
    }
    return marks;
  };

  const getActivityIntensity = (hour) => {
    const hourStart = new Date(date + `T${hour.toString().padStart(2, '0')}:00:00`);
    const hourEnd = new Date(date + `T${hour.toString().padStart(2, '0')}:59:59`);
    const eventsInHour = events.filter(e => {
      const eventTime = new Date(e.timestamp);
      return eventTime >= hourStart && eventTime <= hourEnd;
    });
    return Math.min(eventsInHour.length / 10, 1);
  };

  return (
    <div className={compact ? "space-y-2" : "p-4 space-y-3"}>
      {/* Controls */}
      {!compact && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Timeline</span>
            <span className="text-xs text-gray-500">{date}</span>
            {/* ✅ Playing indicator */}
            {playing && (
              <span className="flex items-center gap-1 text-xs text-blue-600">
                <FiPlay className="w-3 h-3" />
                Playing
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom(Math.max(1, zoom / 2))}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
              disabled={zoom <= 1}
            >
              <FiZoomOut className="w-4 h-4" />
            </button>
            
            <span className="text-xs text-gray-500 min-w-[60px] text-center">
              {zoom === 1 ? "24h" : zoom === 2 ? "12h" : zoom === 4 ? "6h" : "3h"}
            </span>

            <button
              onClick={() => setZoom(Math.min(16, zoom * 2))}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
              disabled={zoom >= 16}
            >
              <FiZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile: Compact controls */}
      {compact && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">{date}</span>
            {/* ✅ Playing indicator */}
            {playing && (
              <span className="flex items-center gap-1 text-xs text-blue-600">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                Playing
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setZoom(Math.max(1, zoom / 2))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
              disabled={zoom <= 1}
            >
              <FiZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setZoom(Math.min(4, zoom * 2))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
              disabled={zoom >= 4}
            >
              <FiZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Timeline Container */}
      <div className="relative">
        {/* Activity Heat Map */}
        <div className="absolute inset-0 h-12 flex">
          {Array.from({ length: 24 }, (_, hour) => {
            const intensity = getActivityIntensity(hour);
            return (
              <div
                key={hour}
                className="flex-1"
                style={{
                  backgroundColor: intensity > 0
                    ? `rgba(59, 130, 246, ${0.1 + intensity * 0.4})`
                    : "transparent",
                }}
              />
            );
          })}
        </div>

        {/* Recordings Bar */}
        <div
          ref={timelineRef}
          className={`relative ${
            compact ? "h-10" : "h-12"
          } bg-gray-200 dark:bg-neutral-800 rounded overflow-hidden cursor-pointer touch-none`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerLeave={() => setHoveredTime(null)}
        >
          {/* Recordings */}
          {recordings.map((rec, idx) => {
            const start = timeToPosition(rec.start_time);
            const end = timeToPosition(rec.end_time);
            const width = end - start;

            return (
              <div
                key={rec.id || idx}
                className="absolute top-0 h-full bg-blue-600"
                style={{
                  left: `${start}%`,
                  width: `${width}%`,
                }}
              />
            );
          })}

          {/* Events */}
          {events.map((event, idx) => {
            const pos = timeToPosition(event.timestamp);
            return (
              <div
                key={event.id || idx}
                className={`absolute top-0 h-full ${
                  compact ? "w-1" : "w-0.5"
                } bg-red-500`}
                style={{ left: `${pos}%` }}
              />
            );
          })}

          {/* Bookmarks */}
          {bookmarks.map((bookmark, idx) => {
            const pos = timeToPosition(bookmark.timestamp);
            return (
              <div
                key={bookmark.id || idx}
                className="absolute top-0 h-full"
                style={{ left: `${pos}%` }}
              >
                <div className={`${
                  compact ? "w-5" : "w-6"
                } h-full bg-yellow-500 opacity-30`} />
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 ${
                  compact
                    ? "border-l-4 border-r-4 border-t-4"
                    : "border-l-8 border-r-8 border-t-8"
                } border-l-transparent border-r-transparent border-t-yellow-500`} />
              </div>
            );
          })}

          {/* ✅ ENHANCED Current Time Indicator */}
          <div
            className="absolute top-0 h-full flex flex-col items-center pointer-events-none z-10"
            style={{ left: `${timeToPosition(currentTime)}%` }}
          >
            {/* Handle - BIGGER and ANIMATED when playing */}
            <div className={`${
              compact ? "w-5 h-5 -mt-1" : "w-4 h-4 -mt-2"
            } bg-white rounded-full shadow-xl border-2 ${
              playing 
                ? "border-blue-600 animate-pulse" 
                : "border-red-600"
            }`} />
            
            {/* Line - THICKER and COLORED when playing */}
            <div className={`${
              compact ? "w-1" : "w-1"
            } flex-1 shadow-lg ${
              playing 
                ? "bg-blue-600" 
                : "bg-red-600"
            }`} />
            
            {/* ✅ Playback indicator at bottom */}
            {playing && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                <div className="flex items-center gap-1 bg-blue-600 text-white px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">
                  <FiPlay className="w-2.5 h-2.5" />
                  <span>Playing</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hour Markers */}
        <div className={`relative ${compact ? "h-4" : "h-6"} flex items-end`}>
          {getHourMarks().map(hour => (
            <div
              key={hour}
              className={`absolute bottom-0 flex flex-col items-center ${
                compact ? "text-[10px]" : "text-xs"
              } text-gray-500`}
              style={{ left: `${(hour / 24) * 100}%` }}
            >
              <div className="w-px h-2 bg-gray-400" />
              <span className="mt-1">
                {hour.toString().padStart(2, '0')}:00
              </span>
            </div>
          ))}
        </div>

        {/* Hover Tooltip */}
        {!compact && hoveredTime && !isDragging && (
          <div
            className="absolute bottom-full mb-2 transform -translate-x-1/2 pointer-events-none z-10"
            style={{ left: `${timeToPosition(hoveredTime)}%` }}
          >
            <div className="bg-black text-white px-3 py-2 rounded-lg shadow-xl text-sm font-medium whitespace-nowrap">
              {hoveredTime.toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {!compact && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 bg-blue-600 rounded" />
            <span>Recordings</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-red-500" />
            <span>Events</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 opacity-30 rounded" />
            <span>Bookmarks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full border-2 ${
              playing ? "border-blue-600 bg-blue-600" : "border-red-600 bg-red-600"
            }`} />
            <span>Current Position {playing && "(Playing)"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
